import type {
  CreateUntrackedAssetCatalogWithTaxonomyDto,
  UntrackedAssetCatalogFilterParams,
  UpdateUntrackedAssetCatalogDto,
  UpdateUntrackedAssetCatalogTaxonomyDto,
} from '@peritasai/types';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { untrackedAssetCatalogRepository } from './repository';

export const untrackedAssetCatalogKeys = {
  all: ['untracked-asset-catalog'] as const,
  lists: () => [...untrackedAssetCatalogKeys.all, 'list'] as const,
  list: (filters: UntrackedAssetCatalogFilterParams) =>
    [...untrackedAssetCatalogKeys.lists(), { filters }] as const,
  taxonomyClasses: () => [...untrackedAssetCatalogKeys.all, 'taxonomy-classes'] as const,
} as const;

export function useUntrackedAssetCatalog(
  filters: UntrackedAssetCatalogFilterParams = {},
  { enabled }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: untrackedAssetCatalogKeys.list(filters),
    queryFn: () => untrackedAssetCatalogRepository.getUntrackedAssetCatalogPaginated(filters),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    enabled,
  });
}

export function useCreateUntrackedAssetCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateUntrackedAssetCatalogWithTaxonomyDto) =>
      untrackedAssetCatalogRepository.createWithTaxonomy(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: untrackedAssetCatalogKeys.lists() });
    },
  });
}

export function useUpdateUntrackedAssetCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateUntrackedAssetCatalogDto }) =>
      untrackedAssetCatalogRepository.updateUntrackedAssetCatalog(id, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: untrackedAssetCatalogKeys.lists() });
    },
  });
}

export function useUpdateTaxonomyLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      catalogItemId,
      dto,
    }: {
      catalogItemId: string;
      dto: UpdateUntrackedAssetCatalogTaxonomyDto;
    }) => untrackedAssetCatalogRepository.updateTaxonomyLink(catalogItemId, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: untrackedAssetCatalogKeys.lists() });
    },
  });
}

export function useInstrumentTaxonomyClasses() {
  return useQuery({
    queryKey: untrackedAssetCatalogKeys.taxonomyClasses(),
    queryFn: () => untrackedAssetCatalogRepository.getInstrumentTaxonomyClasses(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useInstrumentClassImage(imageId: number | null | undefined) {
  return useQuery({
    queryKey: ['instrument-class-image', imageId],
    queryFn: async () => {
      const blob = await untrackedAssetCatalogRepository.fetchClassImage(imageId!);
      return URL.createObjectURL(blob);
    },
    enabled: imageId != null,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}
